// SC-2768 — the History (audit) view's `since` filter + its URL round-trip. Pure +
// exported so the agent bridge (an `open_view` directive → URL) and the ISO discipline
// are unit-testable without a store or a router.
//
// `since` is a SERVER param: the History feed is genuinely paginated (`/history`
// returns 20/page, sorted newest-first), so a lower-bound date can't be applied
// correctly over a single client page. The FE wires `?since=` straight through
// `useHistory` → `historyApi`.
import type { CanvasDirective, HistoryAction } from '../../types';
import type { FacetChoice } from '../catalog/facets/CategoricalFacet';

// ISO-8601 date or datetime — mirrors the BE's `datetime.fromisoformat` acceptance:
// `YYYY-MM-DD`, optionally `T`/space + `HH:MM[:SS[.ffffff]]` + optional `Z` or `±HH:MM`
// offset. Deliberately STRICTER than `Date.parse`, which also accepts locale strings
// like `July 14, 2026` / `07/14/2026` that would 422 the BE's typed `since` datetime —
// so a hand-edited URL is dropped here, not forwarded to fail there.
const ISO_8601_RE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

/** A valid ISO-8601 lower bound, or `null`. Guards the URL read against a
 *  stale/hand-edited `?since=`: a non-ISO or impossible value (`?since=lastweek`,
 *  `July 14, 2026`, `2026-13-45`) is dropped rather than forwarded. The shape check
 *  rejects Date-parseable-but-non-ISO text; the `Date.parse` check then rejects a
 *  well-shaped but impossible date. The agent only ever emits ISO (`_validate_since`,
 *  BE open_view), so this only bites hand-crafted links. */
function validIso(raw: string | null | undefined): string | null {
  return raw && ISO_8601_RE.test(raw) && !Number.isNaN(Date.parse(raw)) ? raw : null;
}

/** Read the `since` lower bound out of the URL; the inverse of {@link writeSince}. */
export function parseSince(sp: URLSearchParams): string | null {
  return validIso(sp.get('since'));
}

/** Write `since` onto a copy of `prev`, preserving unrelated keys (`action`/`page`).
 *  `null` or a non-ISO value clears it. */
export function writeSince(prev: URLSearchParams, since: string | null): URLSearchParams {
  const next = new URLSearchParams(prev);
  const iso = validIso(since);
  if (iso) next.set('since', iso);
  else next.delete('since');
  return next;
}

/** Translate an agent `open_view` history directive (SC-2768) into the History-view URL
 *  query — the same bridge findings/attributes use. An absent/invalid `since` (or a bare
 *  history nav) yields the empty query (the whole feed). The agent directive carries only
 *  `since` today (the `action` facet is human-rail-only until a BE fast-follow makes it
 *  agent-addressable, SC-27xx). */
export function historyDirectiveToSearch(directive: CanvasDirective): string {
  const p = directive.params;
  if (!p || p.view !== 'history') return '';
  return writeSince(new URLSearchParams(), p.since ?? null).toString();
}

// --- The full History facet model (SC-2787) ---------------------------------
// The History rail's two axes: `action` (a SERVER param — the feed is paginated, so a
// stored-action filter is applied server-side) and `since` (also a server param). Both
// live in the URL, so the rail is shareable + refresh-safe and the agent `open_view`
// directive can drive the same params.

// SC-3424 — the ledger-backed feed's full action vocabulary (BE `HistoryAction`).
const ACTION_VALUES: HistoryAction[] = [
  'applied',
  'edited',
  'split',
  'split_discarded',
  'source_refresh',
  'undone',
  'acknowledged',
  'dismissed',
];

export interface HistoryFilters {
  /** History action — multi-select; [] = all actions. */
  action: HistoryAction[];
  /** ISO lower bound on the event time, or null. */
  since: string | null;
}

export const DEFAULT_HISTORY_FILTERS: HistoryFilters = { action: [], since: null };

/** Parse the `action` multi-select, tolerant of BOTH comma-joined (`?action=applied,dismissed`,
 *  what we write) AND repeated params, dropping unknown members and de-duping. */
function parseAction(sp: URLSearchParams): HistoryAction[] {
  const raw = sp
    .getAll('action')
    .flatMap((v) => v.split(','))
    .map((v) => v.trim());
  return [...new Set(raw.filter((v): v is HistoryAction => (ACTION_VALUES as readonly string[]).includes(v)))];
}

/** Read the full filter state out of the URL; the inverse of {@link writeHistoryFilters}. */
export function parseHistoryFilters(sp: URLSearchParams): HistoryFilters {
  return { action: parseAction(sp), since: parseSince(sp) };
}

/** Write the full filter state onto a copy of `prev`, preserving unrelated keys (the
 *  feed's `?page=`). A default axis is deleted, keeping the URL clean. (The caller resets
 *  `page` on a filter change — a narrower filter can leave a stale high page empty.) */
export function writeHistoryFilters(prev: URLSearchParams, filters: HistoryFilters): URLSearchParams {
  const next = writeSince(prev, filters.since);
  if (filters.action.length) next.set('action', filters.action.join(','));
  else next.delete('action');
  return next;
}

/** True when both axes are at their default — drives clean URLs and the "genuinely
 *  empty" vs "filtered to empty" empty-state split. */
export function isDefaultHistoryFilters(f: HistoryFilters): boolean {
  return historyFilterCount(f) === 0;
}

/** Number of active filter selections — the single source for BOTH the rail's header
 *  pill and the toolbar Filters badge, so they can't disagree. */
export function historyFilterCount(f: HistoryFilters): number {
  return f.action.length + (f.since ? 1 : 0);
}

/** Each action's friendly label — the rail's facet options and the table's action chip. */
export const ACTION_LABELS: Record<HistoryAction, string> = {
  applied: 'Applied',
  edited: 'Edited',
  split: 'Split',
  split_discarded: 'Split discarded',
  source_refresh: 'Source refresh',
  undone: 'Undone',
  acknowledged: 'Acknowledged',
  dismissed: 'Dismissed',
};

/** The action facet's options (value + friendly label) for the rail's CategoricalFacet. */
export const ACTION_OPTIONS: FacetChoice[] = ACTION_VALUES.map((value) => ({
  value,
  label: ACTION_LABELS[value],
}));
